import { useSearchSidebarContext } from "@project/contexts/SearchSidebarContext";
import { otherSearchFilters } from "@project/helpers/constants";
import { FilterChipInterface, FilterType, SelectedFiltersInterface } from "@project/interfaces/ISearchPage";
import { useGetConceptsQuery } from "@project/store/conceptsApi";
import { useGetConceptZonesQuery } from "@project/store/conceptZoneApi";
import { useGetGradeLevelsQuery } from "@project/store/gradeLevelsApi";
import { useGetResourceTypesQuery } from "@project/store/resourceTypeApi";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function useFilter() {
  const { data: dataGradeLevels, isLoading: loadingGradeLevels, isFetching: fetchingGradeLevels } = useGetGradeLevelsQuery('');
  const { data: dataConceptZones, isLoading: loadingConceptZones, isFetching: fetchingConceptZones  } = useGetConceptZonesQuery('');
  const { data: dataResourceTypes, isLoading: loadingResourceTypes, isFetching: fetchingResourceTypes  } = useGetResourceTypesQuery('');
  const { data: dataConcepts, isLoading: loadingConcepts, isFetching: fetchingConcepts  } = useGetConceptsQuery('');

  const [gradeLevels, setGradeLevels] = useState<any[]>([]);
  const [conceptZones, setConceptZones] = useState<any[]>([]);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [concepts, setConcepts] = useState<any[]>([]);
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const others = otherSearchFilters;

  const { showSearchBar, selectedFilters, setSelectedFilters, setFilterList, filterList } = useSearchSidebarContext();

  const location = useLocation();
  const navigate = useNavigate();

  const retainFilterRoutes = [
    '/search',
    '/resource'
  ];

  const handleFilter = (filter: FilterChipInterface, type: FilterType) => {
    // Retrieve the current filters for the specified type, defaulting to an empty array if none exist
    let filters = selectedFilters?.[type as keyof SelectedFiltersInterface] || [];
  
    // Check if the filter already exists in the list
    const filterExists = filters.some(existingFilter => existingFilter.id === filter.id);
  
    // If the filter exists, remove it; otherwise, add it to the list
    filters = filterExists 
      ? filters.filter(existingFilter => existingFilter.id !== filter.id) 
      : [...filters, filter];
  
    // Update the selectedFilters state with the new filter list for the specified type
    setSelectedFilters({
      ...selectedFilters,
      [type as keyof SelectedFiltersInterface]: filters
    });

    redirectToSearch();
  };

  const resetFilter = () => {
    setSelectedFilters({});
  }

  const handleSelectAll = (type: FilterType) => {
    setSelectedFilters((prevState) => {
      let updatedFilterArray: FilterChipInterface[] = [];

      if(prevState) {}
      if (type === 'gradeLevels') {
        updatedFilterArray = [...gradeLevels.map(item => ({
          id: item.code,
          name: item.name,
        }))];
        if(prevState && prevState[type]?.length===gradeLevels.length)
          updatedFilterArray = []
      } else if (type === 'conceptZones') {
        updatedFilterArray = [...conceptZones.map(item => ({
          id: item.id,
          name: item.name,
        }))];
        if(prevState && prevState[type]?.length===conceptZones.length)
          updatedFilterArray = []
      } else if (type === 'concepts') {
        updatedFilterArray = [...concepts.map(item => ({
          id: item.id,
          name: item.name,
        }))];
        if(prevState && prevState[type]?.length===concepts.length)
          updatedFilterArray = []
      } else if (type === 'resourceTypes') {
        updatedFilterArray = [...resourceTypes.map(item => ({
          id: item.id,
          name: item.name,
        }))];

        if(prevState && prevState[type]?.length===resourceTypes.length)
          updatedFilterArray = []
      }

      return {
        ...prevState,
        [type]: updatedFilterArray,
      };
    });
    redirectToSearch();
  };

  const redirectToSearch = () => {
    if(location.pathname != '/search')
      navigate('/search');
  }

  useEffect(() => {
    if(dataGradeLevels) setGradeLevels(dataGradeLevels as any);
  },[dataGradeLevels]);

  useEffect(() => {
    if(dataConceptZones) setConceptZones(dataConceptZones as any);
  }, [dataConceptZones])

  useEffect(() => {
    if(dataResourceTypes) {
      setResourceTypes((dataResourceTypes as any[]).map(r=>(
        {
          id: r.code,
          name: r.name
        }
      )));
    }
  }, [dataResourceTypes])

  useEffect(() => {
    if(dataConcepts) setConcepts(dataConcepts as any);
  }, [dataConcepts])

  useEffect(() => {
    if (!loadingGradeLevels && !fetchingGradeLevels &&
      !loadingConceptZones &&
      !fetchingConceptZones &&
      !loadingResourceTypes &&
      !fetchingResourceTypes &&
      !loadingConcepts &&
      !fetchingConcepts) setLoadingFilters(false);
    else
      setLoadingFilters(true);
  }, [
    loadingGradeLevels,
    fetchingGradeLevels,
    loadingConceptZones,
    fetchingConceptZones,
    loadingResourceTypes,
    fetchingResourceTypes,
    loadingConcepts,
    fetchingConcepts,
  ])

  useEffect(() => {
    const newFilterList : any = { ...filterList };
  
    const updateFilter = (key: string, value: any) => {
      if (value && (!newFilterList[key] || newFilterList[key].length <= 0)) {
        newFilterList[key] = value;
      }
    };
  
    updateFilter('gradeLevels', gradeLevels);
    updateFilter('conceptZones', conceptZones);
    updateFilter('resourceTypes', resourceTypes);
    updateFilter('concepts', concepts);
  
    if (JSON.stringify(newFilterList) !== JSON.stringify(filterList)) {
      setFilterList(newFilterList);
    }
  }, [gradeLevels, conceptZones, resourceTypes, concepts, filterList]);

  useEffect(() => {
    // Reset filters and retain on a specified path
    if(!retainFilterRoutes.some(route => location.pathname.startsWith(route))) resetFilter();
  }, [location]);
  
  return {
    gradeLevels,
    conceptZones,
    resourceTypes,
    concepts,
    others,
    showSearchBar,
    loadingFilters,
    handleFilter,
    handleSelectAll,
    resetFilter,
    selectedFilters,
  }
}

export default useFilter;
